import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Playlist from "./sections/Playlist";
import PageDescription from "./sections/PageDescription";
import TracksDetail from "./sections/components/TracksDetail";
import PageContent from "./sections/PageContent";
import InsightSection from "./sections/InsightSection";
import RadioGeoInsightSection from "./sections/RadioGeoInsight";

import StarIcon from "../../../assets/images/admin/Star-outlined.svg";
import DummyTrackCard from "../../../components/DummyTrackCard";
import { PricingPlanModal } from "../../../components/PricingPlanModal";

import * as actions from "../../../redux/actions/dataAnalyst";
import { getCustomerPortalSession } from "../../../redux/actions";

import {
  formatDateToMonthDay,
  getArtistNames,
} from "../../../helper/utility";
import { getIconFromSource, useRadioData } from "./datahooks";
import { useLocation } from "react-router-dom";
import AppTour from "../../../components/AppTour/AppTour";
import { radioPlayTourSteps } from "../../../components/AppTour/ModuleTourSteps";
import { useTrackFirstVisit, ACTIVITY_TYPES } from "../../../hooks/useTrackFirstVisit";
import { useTrackModuleVisit, MODULE_VISIT_TYPES } from "../../../hooks/useTrackModuleVisit";

const PERIOD_OPTIONS = Object.freeze([
  { value: "all", label: "All time performance" },
  { value: "weekly", label: "Weekly performance" },
  { value: "monthly", label: "Monthly performance" },
  { value: "yearly", label: "Yearly performance" },
]);

const SAMPLE_TRACKS = Object.freeze([
  {
    image:
      "https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=80&h=80&fit=crop",
    name: "Summer Vibes",
    artist: "The Wave Makers",
    date: "2024-03-15",
    label: "Ocean Records",
    channel: "Spotify",
    icon: null,
  },
  {
    image:
      "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=80&h=80&fit=crop",
    name: "Midnight Dreams",
    artist: "Luna Eclipse",
    date: "2024-02-20",
    label: "Stellar Music",
    channel: "Apple Music",
    icon: null,
  },
]);

function RadioPlay() {
  const dispatch = useDispatch();
  const { hash } = useLocation()
  const [runTour, setRunTour] = useState(false)
  useTrackFirstVisit(ACTIVITY_TYPES.DATA_ANALYST_RADIO_PLAY_FIRST_VISIT);
  useTrackModuleVisit(MODULE_VISIT_TYPES.DATA_ANALYST, "radioplay");
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const [showPricingPlanModal, setShowPricingPlanModal] = useState(false);

  const [loadArtistData, setLoadArtistData] = useState(false);
  const [loadMilestonesData, setLoadMilestonesData] = useState(false);
  const [loadAudienceData, setLoadAudienceData] = useState(false);

  const hasActiveSubscription = useSelector(
    (state) => state.billing.hasActiveSubscription
  );

  const {
    radioMilestonesData,
    artistStatsBySource,
    artistChartStatsBySource,
    audienceDetails,
    trackStats,
    trackChartStats,
    searchQuery,
  } = useSelector(
    (state) => ({
      radioMilestonesData: state.dataanalyst.radioMilestonesData,
      artistStatsBySource: state.dataanalyst.artistStatsBySource,
      artistChartStatsBySource: state.dataanalyst.artistChartStatsBySource,
      audienceDetails: state.dataanalyst.audienceDetails,
      trackStats: state.dataanalyst.trackStats,
      trackChartStats: state.dataanalyst.trackChartStats,
      searchQuery: state.dataanalyst.searchQuery,
    }),
    shallowEqual
  );

  const radioState = radioMilestonesData?.data
    ? radioMilestonesData
    : Array.isArray(radioMilestonesData)
      ? { data: radioMilestonesData }
      : { data: [] };

  const radioList = radioState?.data || [];

  const [isLoadingMoreRadio, setIsLoadingMoreRadio] = useState(false);

  const loadMoreRadio = useCallback(() => {
    const next = radioState?.nextOffset;
    if (next && !isLoadingMoreRadio) {
      setIsLoadingMoreRadio(true);
      if (searchQuery?.id) {
        dispatch(
          actions.getTrackActivities(
            toast,
            { spotify_track_id: searchQuery.id, source: "radio", offset: next },
            "RADIO_MILESTONE",
            (v) => {
              if (typeof v === "boolean") setIsLoadingMoreRadio(v);
            }
          )
        ).finally(() => setIsLoadingMoreRadio(false));
      } else {
        dispatch(
          actions.getRadioMilestones(
            toast,
            { source: "radio", offset: next },
            (v) => {
              if (typeof v === "boolean") setIsLoadingMoreRadio(v);
            }
          )
        ).finally(() => setIsLoadingMoreRadio(false));
      }
    }
  }, [dispatch, radioState, isLoadingMoreRadio, searchQuery]);

  useEffect(() => {
    if (!hasActiveSubscription) return;

    dispatch(actions.getRadioMilestones(toast, {}, setLoadMilestonesData));
    dispatch(actions.getRadioGeoStats(toast));
    dispatch(
      actions.getAudienceDetails(
        toast,
        { source: "radio" },
        setLoadAudienceData
      )
    );
  }, [hasActiveSubscription, dispatch]);

  useEffect(() => {
    if (!hasActiveSubscription) return;

    dispatch(
      actions.getArtistStatsBySources(
        toast,
        { type: selectedPeriod, source: "radio" },
        setLoadArtistData
      )
    );

    dispatch(
      actions.getArtistChartStatsBySource(toast, {
        type: selectedPeriod,
        source: "radio",
      })
    );
  }, [selectedPeriod, hasActiveSubscription, dispatch]);

  const statsSource = useMemo(() => {
    return searchQuery?.id != null && trackStats
      ? trackStats
      : artistStatsBySource;
  }, [searchQuery?.id, trackStats, artistStatsBySource]);

  const chartSource = useMemo(() => {
    return searchQuery?.id != null && trackChartStats
      ? trackChartStats
      : artistChartStatsBySource?.all_data;
  }, [
    searchQuery?.id,
    trackChartStats,
    artistChartStatsBySource,
  ]);
  const radioMetrics = useRadioData(
    statsSource,
    chartSource,
    audienceDetails,
    selectedPeriod,
    searchQuery?.id
  );

  const onPeriodChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (value === selectedPeriod) return;
      setSelectedPeriod(value);
    },
    [selectedPeriod]
  );

  const handleProClick = useCallback(async () => {
    setLoadMilestonesData(true);
    try {
      if (hasActiveSubscription) {
        await dispatch(getCustomerPortalSession());
      } else {
        setShowPricingPlanModal(true);
      }
    } finally {
      setLoadMilestonesData(false);
    }
  }, [dispatch, hasActiveSubscription]);

  const renderedTracks = useMemo(() => {
    if (hasActiveSubscription && radioList?.length > 0) {
      return radioList.map((item) => (
        <TracksDetail
          key={item._id || item.activity_date}
          trackData={{
            image: item.track_info.avatar,
            name: item.track_info.title,
            artist: getArtistNames(item.track_info.artists),
            streams: item.streams_total,
            label: item.activity_text,
            date: formatDateToMonthDay(item.activity_date),
            icon: getIconFromSource(item.source),
            link: item.track_info.site_url,
          }}
        />
      ));
    }

    return SAMPLE_TRACKS.map((track, index) => (
      <DummyTrackCard
        key={index}
        trackData={track}
        isPro={false}
        onProClick={handleProClick}
      />
    ));
  }, [radioMilestonesData, hasActiveSubscription, handleProClick]);

  useEffect(() => {
    if (hash === "#start-tour") {
      setRunTour(true)
    }
  }, [hash])

  return (
    <div className="DataAnalyst-page py-5">
      <AppTour
        tourSteps={radioPlayTourSteps}
        runTour={runTour}
        setRunTour={setRunTour}
      />
      <div className="container max-w-full">
        <PageDescription
          title="Radio Play"
          description="You’re being played on radio waves and frequencies across the globe."
          withBackOption
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="h-[80vh]" data-tour="geo-data">
            <RadioGeoInsightSection
              hasActiveSubscription={hasActiveSubscription}
              onProClick={handleProClick}
            />
          </div>
          <div data-tour="radio-milestones">
            <Playlist
              icon={StarIcon}
              title="Radio"
              activity_type="RADIO_MILESTONE"
              selectedPlatform="radio"
              onProClick={handleProClick}
              isLoading={loadMilestonesData}
              height="h-[80vh]"
              hasMore={radioState?.hasMore}
              isLoadingMore={isLoadingMoreRadio}
              onLoadMore={loadMoreRadio}
              withSearch
            >
              <div className="flex flex-col gap-2">{renderedTracks}</div>
            </Playlist>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 mt-4">
          <div className="lg:w-[70%]" data-tour="tracks-performance">
            <PageContent
              periodOptions={PERIOD_OPTIONS}
              selectedPeriod={selectedPeriod}
              selectedPlatform="radio"
              onPeriodChange={onPeriodChange}
              isLoading={loadArtistData}
              metrics={radioMetrics}
              hasActiveSubscription={hasActiveSubscription}
              onProClick={handleProClick}
              withSearch
            />
          </div>

          <div className="lg:w-[30%]" data-tour="amplitude-insights">
            <InsightSection
              topTrack={artistStatsBySource?.topTrack}
              hasActiveSubscription={hasActiveSubscription}
              onProClick={handleProClick}
            />
          </div>
        </div>
      </div>

      <PricingPlanModal
        isOpen={showPricingPlanModal}
        onClose={() => setShowPricingPlanModal(false)}
        returnUrl={`${window.location.origin}/admin/data-analyst/radioplay`}
      />
    </div>
  );
}

export default RadioPlay;

